import { IDjangoFontStyleMinimal } from './FontStyles';

export const DJANGO_LICENSE_TYPES = [
    'web',
    'app',
    'broadcast',
    'desktop',
    'epub',
    'server',
    'game',
    'embedding',
] as const;

export type DjangoLicenseType = (typeof DJANGO_LICENSE_TYPES)[number];

export function isDjangoLicenseType(value: string): value is DjangoLicenseType {
    return DJANGO_LICENSE_TYPES.includes(value as DjangoLicenseType);
}

// This corresponse to the store.models.License in Django.
export interface IDjangoLicense {
    active: boolean;
    active_override: boolean;
    // this is a flag inherited from a family that has been marked as no longer available for
    // renewals.
    disable_renewal: boolean;
    end: string | null;
    id: number;
    is_upgrade_or_renewal: boolean;
    license_type: DjangoLicenseType;
    order: number;
    // date string, license end date, format: '2019-09-11';
    price: string;
    price_paid: string;
    // core constraint on the right.
    qty: number;
    // new right id, if license right was edited, I think....
    replaced_by: number | null;
    // date string, license start date, format: '2019-09-11';
    self_hosted: boolean;
    start: string;
    // style id
    style: number;
    style_url: string;
    under_quota: boolean;
    // style version included
    versions: [];
    // this is the number of visits aggregated from the cdn to be used for license enforcement againt the views limit.
    visits: number;
    // license term
    years: number | null;
}

export interface ILicenseAppRight extends IDjangoLicense {
    end: string;
    // remaining years
    years: number;
    license_type: 'app';
}

export interface ILicenseEpubRight extends IDjangoLicense {
    end: string;
    years: number;
    license_type: 'epub';
}

export interface ILicenseDesktopRight extends IDjangoLicense {
    // date string, license end date, desktop licenses are perptual, value will always be null.
    end: null;
    qty: 1;
    years: null;
    // I think for desktop rights this will always be desktop.
    license_type: 'desktop';
}

export interface ILicenseWebRight extends IDjangoLicense {
    end: string;
    // remaining years
    years: number;
    license_type: 'web';
}

export interface ILicenseRights {
    app?: ILicenseAppRight;
    desktop?: ILicenseDesktopRight;
    epub?: ILicenseEpubRight;
    web?: ILicenseWebRight;
    broadcast?: IDjangoLicense;
    server?: IDjangoLicense;
    game?: IDjangoLicense;
    embedding?: IDjangoLicense;
}

export interface IDjangoApi1License {
    style: IDjangoFontStyleMinimal;
    fontRights: ILicenseRights;
}

export function filterIDjangoApi1LicenseToType(
    licenses: IDjangoApi1License[],
    type: DjangoLicenseType
): IDjangoApi1License[] {
    return licenses.filter((l) => !!l.fontRights[type]);
}

export function hasNonPerpetualLicense(
    licenses: IDjangoApi1License[]
): boolean {
    return (
        licenses.filter((license) => {
            let hasEnd = false;
            Object.keys(license.fontRights).forEach((licenseType) => {
                hasEnd = hasEnd || !!license.fontRights[licenseType].end;
            });
            return hasEnd;
        }).length > 0
    );
}

export function hasLimitedLicense(licenses: IDjangoApi1License[]): boolean {
    return licenses.some((license) => {
        return Object.keys(license.fontRights).some((licenseType) => {
            return (
                license.fontRights[licenseType].qty !== -1 &&
                (licenseType === 'epub' ||
                    licenseType === 'desktop' ||
                    !!license.fontRights[licenseType].end)
            );
        });
    });
}

export function hasUpgradeableLicense(licenses: IDjangoApi1License[]): boolean {
    return licenses.some((license) => {
        return Object.keys(license.fontRights).some((licenseType) => {
            return (
                !license.fontRights[licenseType].disable_renewal &&
                (!!license.fontRights[licenseType].end ||
                    license.fontRights[licenseType].qty !== -1)
            );
        });
    });
}

export function canUpgrade(
    license: IDjangoApi1License,
    type: DjangoLicenseType
): boolean {
    const right = license.fontRights[type];
    return !!(right && !right.disable_renewal);
}

export function hasLicenseType(
    license: IDjangoApi1License,
    type: DjangoLicenseType
): boolean {
    const right = license.fontRights[type];
    return !!(right && right.qty);
}

export function licenseIsActive(license: IDjangoApi1License): boolean {
    return !!(
        (license.fontRights.desktop && license.fontRights.desktop.qty) ||
        (license.fontRights.epub && license.fontRights.epub.qty) ||
        (license.fontRights.app && license.fontRights.app.active) ||
        (license.fontRights.broadcast && license.fontRights.broadcast.active) ||
        (license.fontRights.server && license.fontRights.server.qty) ||
        (license.fontRights.game && license.fontRights.game.qty) ||
        (license.fontRights.embedding && license.fontRights.embedding.qty) ||
        (license.fontRights.web && license.fontRights.web.active)
    );
}

export function filterLicensesToType(
    licenses: IDjangoApi1License[],
    licenseType: DjangoLicenseType
): IDjangoApi1License[] {
    return licenses.filter((l) => !!l.fontRights[licenseType]);
}

export function hasWebSelfHostedRight(rights: ILicenseRights): boolean {
    return !!(
        rights.web &&
        rights.web.active &&
        rights.web.qty &&
        rights.web.self_hosted
    );
}

export function hasWebLicenseSelfHosted(
    licenses: IDjangoApi1License[]
): boolean {
    return licenses.some((license) =>
        hasWebSelfHostedRight(license.fontRights)
    );
}

export function hasReplacedLicenseRight(fontRight: ILicenseRights): boolean {
    return Object.values(fontRight).some((right) => right.replaced_by);
}

export function hasReplacedLicense(licenses: IDjangoApi1License[]): boolean {
    return licenses.some((license) =>
        hasReplacedLicenseRight(license.fontRights)
    );
}

function hasUpgradedOrRenewedRight(fontRight: ILicenseRights) {
    return Object.values(fontRight).some(
        (right) => right.is_upgrade_or_renewal
    );
}

export function hasUpgradedOrRenewed(licenses: IDjangoApi1License[]): boolean {
    return licenses.some((license) =>
        hasUpgradedOrRenewedRight(license.fontRights)
    );
}
